import { DOCUMENT, inject, RendererFactory2, Service } from '@angular/core';

import { getDomain } from '@ideals/utils/get-domain';
import { getProjectNameFromUrl } from '@ideals/utils/get-project-name-from-url';

interface LogRocketInitOptions {
  readonly browser?: {
    readonly urlSanitizer?: (url: string) => string;
  };
  readonly dom?: {
    readonly inputSanitizer?: boolean;
    readonly textSanitizer?: boolean;
  };
  readonly network?: {
    readonly requestSanitizer?: (request: Request) => Request;
    readonly responseSanitizer?: (response: Response) => Response;
  };
  readonly rootHostname?: string;
}

declare global {
  interface Window {
    readonly LogRocket: {
      readonly captureException: (error: Error) => void;
      readonly identify: (id: string, params: Record<string, string>) => void;
      readonly init: (key: string, options: LogRocketInitOptions) => void;
      readonly track: (eventName: string, properties?: Record<string, string>) => void;
    };
  }
}

interface UserData {
  readonly email: string;
  readonly id: string;
  readonly name: string;
}

@Service()
export class LogRocketService {
  readonly #document = inject(DOCUMENT);
  readonly #renderer2 = inject(RendererFactory2).createRenderer(null, null);

  #appId?: string;

  addEvents(eventName: string, properties?: Record<string, string>): void {
    if (!this.#appId) {
      return;
    }

    window.LogRocket?.track(eventName, properties);
  }

  addUserTraits(userId: string, traits: Record<string, string>): void {
    if (!this.#appId) {
      return;
    }

    window.LogRocket?.identify(userId, traits);
  }

  captureException(error: Error): void {
    if (!this.#appId) {
      return;
    }

    window.LogRocket?.captureException(error);
  }

  identify(userData: UserData): void {
    if (!this.#appId) {
      return;
    }

    if (!userData) {
      console.warn('LogRocketService.identify: userData is not defined');

      return;
    }

    window.LogRocket?.identify(userData.id, {
      name: userData.name,
      email: userData.email,
    });
  }

  initialize(appId?: string): void {
    if (!appId) {
      return;
    }

    this.#appId = appId;
    this.#createScript(appId);
  }

  #createScript(appId: string): void {
    const script = this.#renderer2.createElement('script') as HTMLScriptElement;
    const scriptSrc = 'https://cdn.lgrckt-in.com/LogRocket.min.js';

    this.#renderer2.setAttribute(script, 'async', '');
    this.#renderer2.setAttribute(script, 'src', scriptSrc);
    this.#renderer2.setAttribute(script, 'crossorigin', 'anonymous');
    this.#renderer2.appendChild(this.#document.head, script);

    script.onload = () => {
      window.LogRocket.init(appId, {
        browser: {
          urlSanitizer: (url) => {
            let sanitizedUrl = url;
            const projectName = getProjectNameFromUrl(url);

            if (projectName) {
              sanitizedUrl = sanitizedUrl.replace(projectName, '***');
            }

            return sanitizedUrl;
          },
        },
        network: {
          requestSanitizer: (request) => {
            return {
              ...request,
              headers: {
                ...request.headers,
                Authorization: '***',
              },
              body: null,
            };
          },
          responseSanitizer: (response) => {
            return {
              ...response,
              body: null,
            };
          },
        },
        rootHostname: `.${getDomain()}`,
      });
    };

    this.#renderer2.appendChild(this.#document.head, script);
  }
}
